import { db } from "./firebaseConfig";
import { collection, query, where, getDocs, doc, onSnapshot, getDoc, setDoc } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";

const storage = getStorage();

/**
 * Convert Firebase Storage URL (gs://...) to HTTP URL
 * @param {string} storageUrl - gs:// path from Firestore
 * @returns {Promise<string>} - HTTP image URL
 */
export const getImageUrl = async (storageUrl) => {
    if (!storageUrl.startsWith("gs://")) return storageUrl; // Already a valid URL

    const path = storageUrl.replace("gs://koku-my.appspot.com/", ""); // Adjust your Firebase bucket
    const imageRef = ref(storage, path);
    return await getDownloadURL(imageRef);
};

/**
 * Fetch the school document ID based on shortName.
 * @param {string} shortName - The shortName of the school.
 * @returns {Promise<string|null>} - Returns school document ID or null if not found.
 */
export const fetchSchoolId = async (shortName) => {
    console.log("🔍 Fetching school ID for:", shortName);

    if (!shortName) {
        console.error("❌ No shortName provided!");
        return null;
    }

    const schoolsRef = collection(db, "schools");
    const q = query(schoolsRef, where("shortName", "==", shortName));

    try {
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            const schoolDoc = querySnapshot.docs[0]; // Get the first matching school
            console.log("✅ School found:", schoolDoc.id);
            return schoolDoc.id; // Return document ID (e.g., "sk_taman_putra_perdana_2")
        } else {
            console.error("❌ School not found for shortName:", shortName);
            return null;
        }
    } catch (error) {
        console.error("❌ Firestore query failed:", error);
        return null;
    }
};


/**
 * Fetch school details using shortName.
 */
export const fetchSchoolDataByShortName = async (shortName, callback) => {
    if (!shortName) {
        console.error("❌ No shortName provided!");
        return () => { }; // Prevent errors
    }

    const schoolsRef = collection(db, "schools");
    const q = query(schoolsRef, where("shortName", "==", shortName));

    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
        const schoolDoc = querySnapshot.docs[0];
        const schoolId = schoolDoc.id;

        return onSnapshot(schoolDoc.ref, (snapshot) => {
            if (snapshot.exists()) {
                callback({ id: schoolId, ...snapshot.data() });
            } else {
                console.warn("⚠️ No school found!");
                callback(null);
            }
        });
    } else {
        console.warn("⚠️ No matching document found.");
        callback(null);
        return () => { }; // Prevent errors
    }
};

/**
 * Fetch club details from Firestore.
 */
export const fetchClubData = async (schoolId, clubName, callback) => {
    if (!schoolId || !clubName) {
        console.error("❌ Missing schoolId or clubName");
        return () => { };
    }

    const clubRef = doc(db, `schools/${schoolId}/clubs`, clubName);

    return onSnapshot(clubRef, (snapshot) => {
        if (snapshot.exists()) {
            callback(snapshot.data());
        } else {
            console.warn(`⚠️ Club '${clubName}' not found`);
            callback(null);
        }
    });
};

/**
 * Fetch fundraiser details from Firestore.
 */
export const fetchFundraiserData = async (schoolId, clubName, callback) => {
    if (!schoolId || !clubName) {
        console.error("❌ Missing schoolId or clubName!");
        return () => { };
    }

    const fundraisersRef = collection(db, `schools/${schoolId}/fundraisers`);
    const q = query(fundraisersRef, where("club_id", "==", clubName));

    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
        const fundraiserDoc = querySnapshot.docs[0];
        return onSnapshot(fundraiserDoc.ref, (snapshot) => {
            if (snapshot.exists()) {
                console.log("✅ Fundraiser found:", snapshot.data());
                callback({ id: fundraiserDoc.id, ...snapshot.data() });
            } else {
                console.warn("⚠️ No fundraiser found for club:", clubName);
                callback(null);
            }
        });
    } else {
        console.warn("⚠️ No fundraiser found for club:", clubName);
        callback(null);
        return () => { };
    }
};



/**
 * Get fundraiser document by school & club.
 */
export const getFundraiserDoc = async (schoolId, clubId) => {
    const docRef = doc(db, `schools/${schoolId}/clubs/${clubId}/fundraiser/main`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
    } else {
        console.warn("⚠️ Fundraiser doc not found.");
        return null;
    }
};

/**
 * Update fundraiser document.
 */
export const updateFundraiserDoc = async (schoolId, clubId, data) => {
    const docRef = doc(db, `schools/${schoolId}/clubs/${clubId}/fundraiser/main`);
    await setDoc(docRef, data, { merge: true });
};


/**
 * Fetch all clubs managed by a specific teacher.
 */
export const fetchClubsByTeacher = async (teacherUid, callback) => {
    const schoolsRef = collection(db, "schools");
    const schoolSnapshot = await getDocs(schoolsRef);

    let unsubscribeFunctions = [];

    schoolSnapshot.forEach((schoolDoc) => {
        const schoolId = schoolDoc.id;
        const clubsRef = collection(db, `schools/${schoolId}/clubs`);
        const q = query(clubsRef, where("teacherUid", "==", teacherUid));

        const unsub = onSnapshot(q, (snapshot) => {
            const results = [];
            snapshot.forEach((doc) => {
                results.push({
                    id: doc.id,
                    schoolId,
                    schoolName: schoolDoc.data().name,
                    ...doc.data(),
                });
            });

            callback(results);
        });

        unsubscribeFunctions.push(unsub);
    });

    // Return function to unsubscribe all
    return () => unsubscribeFunctions.forEach((unsub) => unsub());
};


/**
 * Get club document by schoolId & clubId
 */
export const getClubDoc = async (schoolId, clubId) => {
    const docRef = doc(db, `schools/${schoolId}/clubs/${clubId}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
    } else {
        console.warn("⚠️ Club document not found.");
        return null;
    }
};

/**
 * Update club document
 */
export const updateClubDoc = async (schoolId, clubId, data) => {
    const docRef = doc(db, `schools/${schoolId}/clubs/${clubId}`);
    await setDoc(docRef, data, { merge: true });
};
